import { ActivityLevel, BodyCondition, TreatFrequency, Sex } from "@/helpers/enums";

/* -------------------------------------------------------
 * Pronoun
 * -----------------------------------------------------*/
export type Pronoun = "his" | "her" | "their";

export function getPronoun(sex?: Sex | string): Pronoun {
    if (sex === Sex.MALE) return "his";
    if (sex === Sex.FEMALE) return "her";
    return "their";
}

/* -------------------------------------------------------
 * Age category — calorieCalculator.ts's isPuppy() is the only
 * age split that actually affects the maths (<=12 months).
 * No senior bucket exists in the calculator.
 * -----------------------------------------------------*/
export type AgeCategory = "puppy" | "adult";

export function getAgeCategory(years: number, months: number): AgeCategory {
    const totalMonths = (Number(years) || 0) * 12 + (Number(months) || 0);
    return totalMonths <= 12 ? "puppy" : "adult";
}

/* -------------------------------------------------------
 * Copy variants — keyed on real enum members
 * -----------------------------------------------------*/
const WEIGHT = (name: string) => `Portion size tailored to ${name}'s exact weight.`;

const BODY_TYPE: Record<BodyCondition, (name: string, p: Pronoun) => string> = {
    [BodyCondition.UNDERWEIGHT]: (name, p) => `Precisely portioned to help ${name} reach ${p} ideal weight.`,
    [BodyCondition.IDEAL]: (name, p) => `Precisely portioned to help ${name} maintain ${p} ideal weight.`,
    [BodyCondition.OVERWEIGHT]: (name, p) => `Precisely portioned to help ${name} get back to ${p} ideal weight.`,
};

const AGE: Record<AgeCategory, (name: string) => string> = {
    puppy: (name) => `Balanced to support ${name} through this fast-growing stage.`,
    adult: (name) => `Completely balanced to keep ${name} as healthy as possible, for as long as possible.`,
};

const TREATS: Record<TreatFrequency, () => string> = {
    [TreatFrequency.HEAVY_SNACKER]: () => `Counted into the daily total, so treats don't tip the balance.`,
    [TreatFrequency.OCCASIONAL]: () => `Counted into the daily total, so nothing doubles up.`,
    [TreatFrequency.NEVER]: () => `No treats to account for — every gram goes to the main meal.`,
};

const ACTIVITY: Record<ActivityLevel, () => string> = {
    [ActivityLevel.LOW]: () => `That's built into the maths, even on quieter days.`,
    [ActivityLevel.MEDIUM]: () => `That burns more than most people expect. It's in the maths.`,
    [ActivityLevel.HIGH]: () => `That's a lot of fuel to replace — it's all in the maths.`,
};

const GRAMS = () => `Split across two meals. Nothing to weigh out.`;

/* -------------------------------------------------------
 * Main entry point
 * -----------------------------------------------------*/
export function getStatDescriptions(opts: {
    name: string;
    pronoun: Pronoun;
    bodyCondition?: BodyCondition | string;
    ageCategory: AgeCategory;
    treatFrequency?: TreatFrequency | string;
    activityLevel?: ActivityLevel | string;
}) {
    const { name, pronoun, bodyCondition, ageCategory, treatFrequency, activityLevel } = opts;

    return {
        weight: WEIGHT(name),
        bodyType: (BODY_TYPE[bodyCondition as BodyCondition] ?? BODY_TYPE[BodyCondition.IDEAL])(name, pronoun),
        age: AGE[ageCategory](name),
        treats: (TREATS[treatFrequency as TreatFrequency] ?? TREATS[TreatFrequency.OCCASIONAL])(),
        activity: (ACTIVITY[activityLevel as ActivityLevel] ?? ACTIVITY[ActivityLevel.MEDIUM])(),
        grams: GRAMS(),
    };
}